import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ChannelFilters, DEFAULT_CHANNEL_FILTERS } from '../../../domain';
import { useChannelCategories } from '../../hooks/useChannelCategories';

import { useChannels } from '../../hooks/useChannels';
import { Pagination } from '../../types/Pagination';
import { AdvertEntity, AttachmentProps } from '../../../domain';
import { debounce } from '../../../services/Debounce.service';

import { ContentStep, ChannelsStep, ReviewStep, TelegramAccountsStep } from './Steps';
import GenericWizard, { WizardStep } from '../GenericWizard';

export type AdvertWizardProps = {
  advertModel: AdvertEntity;
  isAdvertLoading: boolean;
  checkedChannels?: Record<string, boolean>;
  showTelegramAccountsStep?: boolean;
  checkedTelegramAccounts?: Record<string, boolean>;
  isSubmitting: boolean;
  pageTitle: string;
  submitButtonText: string;

  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string) => void;
  onUploadFile: ({ file, onSuccess }: { file: File; onSuccess: () => void }) => void;
  onRemoveAttachment: (attachment: AttachmentProps) => void;
  onReorderAttachments: (newOrder: AttachmentProps[]) => void;
  onCheckChannels: (checkedChannels: Record<string, boolean>) => void;
  onCheckTelegramAccounts?: (checkedTelegramAccounts: Record<string, boolean>) => void;
  onSubmit: () => void;
  onBackClick?: () => void;
};

const AdvertWizard: React.FC<AdvertWizardProps> = ({
  advertModel,
  isAdvertLoading,
  checkedChannels = {},
  showTelegramAccountsStep = false,
  checkedTelegramAccounts = {},
  isSubmitting,
  pageTitle,
  submitButtonText,
  onTitleChange,
  onDescriptionChange,
  onUploadFile,
  onRemoveAttachment,
  onReorderAttachments,
  onCheckChannels,
  onCheckTelegramAccounts,
  onSubmit,
  onBackClick
}) => {
  const channelsModern = useChannels({ loadOnMount: true });
  const channels = channelsModern.channels;
  const channelsIsLoading = channelsModern.isLoading;
  const channelsPagination = channelsModern.pagination as Pagination;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { categories: channelCategories } = useChannelCategories();
  const [channelFilters, setChannelFilters] = useState<ChannelFilters>(DEFAULT_CHANNEL_FILTERS);
  const [hasTelegramAccounts, setHasTelegramAccounts] = useState<boolean>(true);

  const handleFiltersChange = (filters: ChannelFilters) => {
    setChannelFilters(filters);
    channelsModern.refresh(1, channelsPagination?.perPage || 10, filters);
  };

  // «Выбрать все» обязано учитывать текущие фильтры, иначе выберутся каналы вне выдачи
  const handleSelectAll = () => {
    channelsModern.fetchAll(channelFilters)
      .then(allChannels => {
        const updated = { ...checkedChannels };
        allChannels.forEach(ch => { updated[ch.uuid] = true; });
        onCheckChannels(updated);
      })
  .catch(() => message.error(t('channels-fetch-all-failed', { ns: 'errors' })));
  };

  const handlePageChange = (page: number, pageSize: number) => {
    channelsModern.refresh(page, pageSize, channelFilters);
  };

  const handleLoadMore = () => {
    channelsModern.refresh((channelsPagination?.page || 1) + 1, channelsPagination?.perPage || 10, channelFilters, true);
  };

  const goBack = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate('/adverts');
    }
  };

  const hasCheckedTelegramAccounts = Object.values(checkedTelegramAccounts).some(Boolean);

  const wizardSteps: WizardStep[] = [
    {
      id: 'content',
      title: t('advert-step-content', { ns: 'labels' }),
      description: t('advert-step-content-description', { ns: 'labels' }),
      component: (
        <ContentStep
          advertModel={advertModel}
          isLoading={isAdvertLoading}
          isSubmitting={isSubmitting}
          onTitleChange={onTitleChange}
          onDescriptionChange={onDescriptionChange}
          onUploadFile={onUploadFile}
          onRemoveAttachment={onRemoveAttachment}
          onReorderAttachments={onReorderAttachments}
        />
      )
    },
    {
      id: 'channels',
      title: t('advert-step-channels', { ns: 'labels' }),
      description: t('advert-step-channels-description', { ns: 'labels' }),
      component: (
        <ChannelsStep
          channels={channels}
          categories={channelCategories}
          isLoading={isAdvertLoading || channelsIsLoading}
          pagination={channelsPagination}
          checkedChannels={checkedChannels}
          filters={channelFilters}
          onCheckChannels={onCheckChannels}
          onPageChange={handlePageChange}
          onLoadMore={handleLoadMore}
          onSelectAll={handleSelectAll}
          onFiltersChange={handleFiltersChange}
          onPreviewCount={channelsModern.countChannels}
        />
      )
    },
    ...(showTelegramAccountsStep ? [{
      id: 'telegram-accounts',
      title: t('advert-step-telegram-accounts', { ns: 'labels' }),
      description: t('advert-step-telegram-accounts-description', { ns: 'labels' }),
      component: (
        <TelegramAccountsStep
          checkedTelegramAccounts={checkedTelegramAccounts}
          onCheckTelegramAccounts={(checked) => onCheckTelegramAccounts?.(checked)}
          onAvailabilityChange={setHasTelegramAccounts}
        />
      )
    }] : []),
    {
      id: 'review',
      title: t('advert-step-check', { ns: 'labels' }),
      description: t('advert-step-check-description', { ns: 'labels' }),
      component: (
  <ReviewStep
          advertModel={advertModel}
          checkedChannels={checkedChannels}
        />
      )
    }
  ];

  const validateStep = (currentStep: number) => {
    const titleInvalid = !(advertModel.title.Value || '').trim();
    const filesTooMany = (advertModel.attachments || []).length > 10;
    const telegramAccountsInvalid = wizardSteps[currentStep]?.id === 'telegram-accounts' && hasTelegramAccounts && !hasCheckedTelegramAccounts;
    return titleInvalid || filesTooMany || telegramAccountsInvalid;
  };

  return (
    <GenericWizard
      steps={wizardSteps}
      pageTitle={pageTitle}
      submitButtonText={submitButtonText}
      backButtonText={t('back', { ns: 'buttons' })}
      nextButtonText={t('next', { ns: 'buttons' })}
      backLinkButtonText={t('back', { ns: 'buttons' })}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      onBackClick={goBack}
      shouldDisableNext={validateStep}
      options={{
        scrollToTop: true
      }}
    />
  );
};

export default AdvertWizard;